import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  describeSessionFailure,
  readSession,
  sessionLost,
  SessionRequestError,
  signIn as signInRequest,
  signOut as signOutRequest,
  type SessionData,
} from './client';
import { createInMemoryDrafts, SIGN_IN_IDENTIFIER_DRAFT } from './drafts';
import { SignInGate } from './SignInGate';

/**
 * The session boundary (P2-T2).
 *
 * One place owns the session's client-side state, and everything private lives behind
 * it:
 *
 * - **An unauthenticated visitor sees the gate and nothing else.** The boundary decides
 *   what renders, so no private component is even mounted without a session — there is
 *   no skeleton, no placeholder and no cached private render to leak.
 * - **The CSRF token and every draft are in-memory only.** They are React state and a
 *   `Map`; the one thing the browser persists is the API's own HttpOnly session cookie.
 * - **A lost session hides private data and keeps only the same-owner draft.** A 401
 *   (`sessionLost`) or a bootstrap that reports `authenticated: false` while this tab
 *   believed it was signed in both collapse the boundary to the recovering gate, with
 *   the authenticated cache dropped and nothing written down.
 * - **Logout and owner switch clear the cache and the drafts.** Sign-out revokes the
 *   session server-side, clears the whole query cache and drops every draft; signing in
 *   as a different owner drops the previous owner's drafts before anything renders.
 *
 * The contract is IMPLEMENTATION-CONTRACTS §6 (queries and cache consistency) and §7.
 */

/** The one cache key this boundary owns. P4's financial queries namespace under it. */
const SESSION_QUERY_KEY = ['session', 'current'] as const;

export interface SessionDraft {
  get(key: string): string | undefined;
  set(key: string, value: string): void;
}

export interface AuthenticatedSession {
  readonly ownerId: string;
  /**
   * The session's CSRF token, for the writes that require it (P4's ledger mutations).
   *
   * It is handed out here rather than read from the cache by each caller so that there is
   * one place it enters the app and one place it leaves; it is never written to storage.
   */
  readonly csrfToken: string | undefined;
  /** This owner's in-memory drafts. Nothing here is persisted. */
  readonly draft: SessionDraft;
  /** Private reads run inside this owner's namespace. */
  signOut(): Promise<void>;
  /** Re-read the session; a refusal or a lost session runs the recovery path. */
  revalidate(): Promise<void>;
  /** A private request answered 401: hide private data and keep only this owner's draft. */
  reportSessionLost(): void;
}

const SessionContext = createContext<AuthenticatedSession | undefined>(undefined);

export function useAuthenticatedSession(): AuthenticatedSession {
  const session = useContext(SessionContext);
  if (session === undefined) {
    throw new Error('useAuthenticatedSession() was used outside the authenticated shell');
  }
  return session;
}

export interface SessionBoundaryProps {
  readonly children: ReactNode;
}

/**
 * The signed-in owner of a session state, or `undefined` when there is no session.
 *
 * The wire schema states `authenticated` and `owner` independently, so the pair is
 * reduced here once instead of being assumed at each use.
 */
function ownerIdOf(data: SessionData | undefined): string | undefined {
  if (data === undefined || data.authenticated !== true) return undefined;
  return data.owner?.id;
}

export function SessionBoundary({ children }: SessionBoundaryProps) {
  const queryClient = useQueryClient();
  const drafts = useMemo(() => createInMemoryDrafts(), []);
  /** The owner this tab was last signed in as, held in memory for the recovery gate. */
  const signedInOwner = useRef<string | undefined>(undefined);
  const [expired, setExpired] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [pending, setPending] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>(undefined);

  const session = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: ({ signal }) => readSession(signal),
    // A bootstrap is how this client learns the session is gone, so it is re-read on
    // focus rather than trusted indefinitely (IMPLEMENTATION-CONTRACTS §6).
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const refetch = session.refetch;
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    // A page the browser takes out of its back/forward cache comes back with the render it
    // was frozen with — and that render belongs to whatever session the tab had at the
    // time. Re-read the session the moment the page is restored, and show nothing private
    // until it answers: that is what keeps browser back from presenting one owner's shell
    // to another owner.
    const onPageShow = (event: PageTransitionEvent): void => {
      if (!event.persisted) return;
      setRestoring(true);
      void refetchRef.current().finally(() => setRestoring(false));
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const data: SessionData | undefined = session.data;

  useEffect(() => {
    if (session.isPending || session.isError) return;
    const ownerId = ownerIdOf(data);
    if (ownerId !== undefined) {
      signedInOwner.current = ownerId;
      setExpired(false);
      return;
    }
    // The server says this tab is not signed in. If it was, that is the session ending:
    // private data goes away and only this owner's in-memory draft stays.
    if (signedInOwner.current !== undefined) setExpired(true);
  }, [data, session.isPending, session.isError]);

  const reportSessionLost = useCallback((): void => {
    setExpired(true);
    // The cached session is no longer trustworthy: drop it so nothing private renders.
    // The drafts are deliberately *not* cleared — the same owner may reauthenticate.
    queryClient.setQueryData(SESSION_QUERY_KEY, undefined);
  }, [queryClient]);

  const revalidate = useCallback(async (): Promise<void> => {
    try {
      const fresh = await readSession();
      queryClient.setQueryData(SESSION_QUERY_KEY, fresh);
    } catch (error) {
      if (error instanceof SessionRequestError && sessionLost(error.kind)) {
        reportSessionLost();
        return;
      }
      throw error;
    }
  }, [queryClient, reportSessionLost]);

  const signIn = useCallback(
    async (email: string, password: string): Promise<void> => {
      const csrfToken = data?.csrfToken;
      if (csrfToken === undefined) {
        setActionError(describeSessionFailure('unavailable', 'sign-in'));
        return;
      }
      setPending(true);
      setActionError(undefined);
      try {
        const fresh = await signInRequest({ email, password }, csrfToken);
        const ownerId = fresh.owner?.id;
        if (fresh.authenticated !== true || ownerId === undefined) {
          setActionError(describeSessionFailure('unavailable', 'sign-in'));
          return;
        }
        const previous = signedInOwner.current;
        if (previous !== undefined && previous !== ownerId) drafts.clearOwner(previous);
        // The one thing this tab keeps for a lost session: the identifier its owner
        // typed, held in memory so the recovery gate can offer it back.
        drafts.set(ownerId, SIGN_IN_IDENTIFIER_DRAFT, email);
        signedInOwner.current = ownerId;
        setExpired(false);
        queryClient.setQueryData(SESSION_QUERY_KEY, fresh);
      } catch (error) {
        setActionError(
          error instanceof SessionRequestError
            ? describeSessionFailure(error.kind, 'sign-in')
            : describeSessionFailure('unavailable', 'sign-in'),
        );
      } finally {
        setPending(false);
      }
    },
    [data?.csrfToken, drafts, queryClient],
  );

  const signOut = useCallback(async (): Promise<void> => {
    const csrfToken = data?.csrfToken;
    setPending(true);
    setActionError(undefined);
    try {
      if (csrfToken !== undefined) await signOutRequest(csrfToken);
    } catch {
      // A session that is already gone is still signed out; the cache and drafts below
      // have to go either way.
    } finally {
      // Logout clears the whole cache and every draft: nothing of this owner survives.
      queryClient.clear();
      drafts.clear();
      signedInOwner.current = undefined;
      setExpired(false);
      try {
        queryClient.setQueryData(SESSION_QUERY_KEY, await readSession());
      } catch {
        void session.refetch();
      }
      setPending(false);
    }
  }, [data?.csrfToken, drafts, queryClient, session]);

  const ownerId = ownerIdOf(data);

  const draft = useMemo<SessionDraft>(
    () => ({
      get: (key) => drafts.get(ownerId ?? '', key),
      set: (key, value) => drafts.set(ownerId ?? '', key, value),
    }),
    [drafts, ownerId],
  );

  const sessionApi = useMemo<AuthenticatedSession | undefined>(
    () =>
      ownerId === undefined
        ? undefined
        : { ownerId, csrfToken: data?.csrfToken, draft, signOut, revalidate, reportSessionLost },
    [ownerId, data?.csrfToken, draft, signOut, revalidate, reportSessionLost],
  );

  // A restored page shows nothing private until the session has been re-read: the render it
  // came back with is not evidence that it is still this owner's.
  if (session.isPending || restoring) {
    return (
      <main className="shell">
        <header>
          <h1>Saldo</h1>
        </header>
        <p role="status" data-testid="session-checking">
          Checking your session…
        </p>
      </main>
    );
  }

  if (session.isError) {
    const failure =
      session.error instanceof SessionRequestError
        ? describeSessionFailure(session.error.kind, 'session')
        : describeSessionFailure('unavailable', 'session');
    return (
      <main className="shell">
        <header>
          <h1>Saldo</h1>
        </header>
        <p className="status-bad" role="alert" data-testid="session-unreachable">
          {failure}
        </p>
        <p>
          <button type="button" onClick={() => void session.refetch()}>
            Try again
          </button>
        </p>
      </main>
    );
  }

  // The private shell renders only for a session that is live: a known-ended session
  // never gets a private render, even while its last response is still in memory.
  if (!expired && sessionApi !== undefined) {
    return <SessionContext.Provider value={sessionApi}>{children}</SessionContext.Provider>;
  }

  return (
    <SignInGate
      expired={expired}
      pending={pending}
      error={actionError}
      defaultEmail={
        expired && signedInOwner.current !== undefined
          ? drafts.get(signedInOwner.current, SIGN_IN_IDENTIFIER_DRAFT)
          : undefined
      }
      onSubmit={(email, password) => void signIn(email, password)}
    />
  );
}
